import React, { useRef, useState } from 'react';
import { Play } from 'lucide-react';
import { API } from '@/lib/site';

/**
 * Homepage promotional video section.
 * - Two-column: copy left, video right (stacks on mobile with copy first)
 * - White background, black/white/gold branding, rounded corners + soft shadow
 * - preload="metadata" — only KB load until user clicks play (progressive faststart MP4)
 * - Never autoplays; standard controls; custom gold play overlay before first play
 * - Accessible label
 */
export default function HomeVideoSection() {
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);

  const videoSrcMp4 = `${API}/media/videos/brp-overview.mp4`;
  const videoSrcWebm = `${API}/media/videos/brp-overview.webm`;
  const posterSrc = `${API}/static/videos/brp-overview-poster.jpg`;

  const handlePlayOverlay = () => {
    const el = videoRef.current;
    if (!el) return;
    setStarted(true);
    const p = el.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        /* If autoplay-with-sound is blocked, user click on native controls will play */
      });
    }
  };

  return (
    <section
      className="py-20 md:py-28 px-6 md:px-10 bg-white"
      data-testid="home-video-section"
      aria-labelledby="home-video-heading"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Copy — appears first on mobile, left on desktop */}
        <div className="lg:col-span-5 order-1 lg:order-1">
          <p className="brp-overline">Workforce Performance In Action</p>
          <h2
            id="home-video-heading"
            className="mt-4 font-display uppercase tracking-tight font-bold text-4xl md:text-5xl lg:text-6xl leading-[0.95] text-[#0A0A0A]"
          >
            See Workforce Performance in <span className="text-[#D4AF37]">Action.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-[#555]">
            Remote and hybrid work changed how employees connect, perform, and stay engaged. See how Ball Raw Performance helps organizations create stronger workforce experiences.
          </p>
          <p className="mt-4 text-sm text-[#0A0A0A] font-display uppercase tracking-[0.18em]">
            Watch the overview.
          </p>
          <span className="brp-divider-gold mt-8" />
        </div>

        {/* Video — appears second on mobile, right on desktop */}
        <div className="lg:col-span-7 order-2 lg:order-2">
          <div
            className="relative overflow-hidden rounded-2xl border border-[#E5E5E5] bg-[#0A0A0A]"
            style={{
              boxShadow:
                '0 24px 64px rgba(10, 10, 10, 0.14), 0 4px 16px rgba(10, 10, 10, 0.08)',
            }}
          >
            <div className="relative w-full" style={{ aspectRatio: '16 / 9' }}>
              <video
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-cover"
                poster={posterSrc}
                preload="metadata"
                controls
                playsInline
                aria-label="Ball Raw Performance Workforce Performance Overview"
                title="Ball Raw Performance Workforce Performance Overview"
                data-testid="home-video-player"
                onPlay={() => setStarted(true)}
              >
                <source src={videoSrcMp4} type="video/mp4" />
                <source src={videoSrcWebm} type="video/webm" />
                Your browser does not support the video tag.
              </video>

              {/* Custom play overlay — hidden after playback starts */}
              {!started && (
                <button
                  type="button"
                  onClick={handlePlayOverlay}
                  className="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/15 transition-colors duration-300 group"
                  aria-label="Play video: Ball Raw Performance Workforce Performance Overview"
                  data-testid="home-video-play-overlay"
                >
                  <span className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#D4AF37] group-hover:bg-[#B8962E] flex items-center justify-center shadow-[0_10px_40px_rgba(0,0,0,0.35)] transition-all duration-300 group-hover:scale-105">
                    <Play size={34} strokeWidth={2} className="text-[#0A0A0A] ml-1" fill="#0A0A0A" />
                  </span>
                </button>
              )}
            </div>
          </div>
          <p className="mt-4 text-xs uppercase tracking-[0.22em] font-display text-[#888]">
            Ball Raw Performance Overview
          </p>
        </div>
      </div>
    </section>
  );
}
